const STORAGE_SECRET = import.meta.env.VITE_STORAGE_SECRET ?? 'inspecta-mobile-secret';
const runtimeCrypto = globalThis.crypto;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const base64Encode = (buffer: ArrayBuffer | Uint8Array) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const base64Decode = (value: string) => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

let cachedCryptoKey: CryptoKey | null = null;

const getCryptoKey = async () => {
  if (cachedCryptoKey) {
    return cachedCryptoKey;
  }

  if (!runtimeCrypto?.subtle) {
    throw new Error('Crypto-API nicht verfügbar');
  }

  const secretBuffer = await runtimeCrypto.subtle.digest('SHA-256', textEncoder.encode(STORAGE_SECRET));
  cachedCryptoKey = await runtimeCrypto.subtle.importKey('raw', secretBuffer, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt'
  ]);
  return cachedCryptoKey;
};

const ensureClientSide = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const secureStorage = {
  async setItem<TValue>(key: string, value: TValue): Promise<void> {
    if (!ensureClientSide()) {
      return;
    }

    if (!runtimeCrypto?.subtle) {
      window.localStorage.setItem(key, JSON.stringify(value));
      return;
    }

    const iv = runtimeCrypto.getRandomValues(new Uint8Array(12));
    const encodedValue = textEncoder.encode(JSON.stringify(value));
    const cryptoKey = await getCryptoKey();
    const encryptedBuffer = await runtimeCrypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, encodedValue);

    const payload = `${base64Encode(iv)}.${base64Encode(encryptedBuffer)}`;
    window.localStorage.setItem(key, payload);
  },

  async getItem<TValue>(key: string): Promise<TValue | null> {
    if (!ensureClientSide()) {
      return null;
    }

    const payload = window.localStorage.getItem(key);

    if (!payload) {
      return null;
    }

    try {
      if (!runtimeCrypto?.subtle) {
        return JSON.parse(payload) as TValue;
      }

      const [ivPart, dataPart] = payload.split('.');
      if (!ivPart || !dataPart) {
        throw new Error('Invalid payload');
      }

      const iv = base64Decode(ivPart);
      const encryptedData = base64Decode(dataPart);
      const cryptoKey = await getCryptoKey();

      const decrypted = await runtimeCrypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, encryptedData);
      const decoded = textDecoder.decode(new Uint8Array(decrypted));
      return JSON.parse(decoded) as TValue;
    } catch (error) {
      console.warn('Konnte gespeicherte Sitzung nicht entschlüsseln.', error);
      await this.removeItem(key);
      return null;
    }
  },

  async removeItem(key: string): Promise<void> {
    if (!ensureClientSide()) {
      return;
    }
    window.localStorage.removeItem(key);
  }
};
